import { Objective1, Status1 } from '../types';

export type StatusAnalysis = {
  type: 'actual' | 'planning' | 'none';
  statusCode: Status1['statusCode'] | null;
  date: string | null;
  displayName: string;
};

export type StatusCounts = {
  green: number;
  yellow: number;
  red: number;
  planned: number;
  tbd: number;
};

/**
 * Analyzes an objective's statuses and returns the latest meaningful status
 * Prioritizes actual work status (g,y,r,G,Y,R) over planning status (p,?)
 *
 * @param objective - The objective to analyze
 * @returns The latest meaningful status
 */
export function analyzeObjectiveStatus(objective: Objective1): StatusAnalysis {
  if (!objective.statuses || objective.statuses.length === 0) {
    return {
      type: 'none',
      statusCode: null,
      date: null,
      displayName: 'No status reported',
    };
  }

  // Separate actual work statuses from planning statuses
  const actualStatuses = objective.statuses.filter(
    (status) => status.statusCode && ['g', 'y', 'r', 'G', 'Y', 'R'].includes(status.statusCode),
  );

  const planningStatuses = objective.statuses.filter(
    (status) => status.statusCode && ['p', '?'].includes(status.statusCode),
  );

  let latestStatus = null;
  let type: 'actual' | 'planning' = 'actual';

  // Prioritize actual work status over planning status
  if (actualStatuses.length > 0) {
    latestStatus = actualStatuses[actualStatuses.length - 1];
    type = 'actual';
  } else if (planningStatuses.length > 0) {
    latestStatus = planningStatuses[planningStatuses.length - 1];
    type = 'planning';
  }

  if (!latestStatus) {
    return {
      type: 'none',
      statusCode: null,
      date: null,
      displayName: 'No status reported',
    };
  }

  // Get display name for status
  let displayName = '';
  switch (latestStatus.statusCode) {
    case 'g':
    case 'G':
      displayName = 'green';
      break;
    case 'y':
    case 'Y':
      displayName = 'yellow';
      break;
    case 'r':
    case 'R':
      displayName = 'red';
      break;
    case 'p':
      displayName = 'planned';
      break;
    case '?':
      displayName = 'TBD';
      break;
    default:
      displayName = 'unknown';
  }

  const date = latestStatus.forIntervalEnding
    ? new Date(latestStatus.forIntervalEnding).toLocaleDateString()
    : null;

  return {
    type,
    statusCode: latestStatus.statusCode,
    date,
    displayName,
  };
}

/**
 * Counts latest statuses reported across multiple objectives.
 *
 * @param objectives - The objectives to count
 * @returns The counts of statuses
 */
export function countObjectiveStatuses(objectives: Objective1[]): StatusCounts {
  const counts: StatusCounts = {
    green: 0,
    yellow: 0,
    red: 0,
    planned: 0,
    tbd: 0,
  };

  objectives.forEach((objective) => {
    const analysis = analyzeObjectiveStatus(objective);

    switch (analysis.type) {
      case 'actual':
        if (analysis.statusCode === 'g' || analysis.statusCode === 'G') {
          counts.green++;
        } else if (analysis.statusCode === 'y' || analysis.statusCode === 'Y') {
          counts.yellow++;
        } else if (analysis.statusCode === 'r' || analysis.statusCode === 'R') {
          counts.red++;
        }
        break;
      case 'planning':
        if (analysis.statusCode === 'p') {
          counts.planned++;
        } else if (analysis.statusCode === '?') {
          counts.tbd++;
        }
        break;
      case 'none':
        counts.tbd++;
        break;
    }
  });

  return counts;
}

/**
 * Formats status counts into a readable string for announcements
 */
export function formatStatusCounts(counts: StatusCounts, totalCount: number): string {
  const statusParts: string[] = [];

  if (counts.tbd > 0) {
    statusParts.push(`${counts.tbd} of ${totalCount} TBD`);
  }
  if (counts.planned > 0) {
    statusParts.push(`${counts.planned} of ${totalCount} rows planned`);
  }
  if (counts.green > 0) {
    statusParts.push(`${counts.green} of ${totalCount} rows green`);
  }
  if (counts.yellow > 0) {
    statusParts.push(`${counts.yellow} of ${totalCount} rows yellow`);
  }
  if (counts.red > 0) {
    statusParts.push(`${counts.red} of ${totalCount} rows red`);
  }

  return statusParts.join(', ');
}

/**
 * Formats child status counts for parent objective announcements
 * Now includes all status types: green, yellow, red, planned, tbd
 */
export function formatChildStatusCounts(
  counts: StatusCounts,
  format: 'default' | 'short' = 'default',
): string {
  const statusParts: string[] = [];

  // Progress statuses (prioritized first)
  if (counts.green > 0) {
    statusParts.push(format === 'short' ? `${counts.green} g` : `${counts.green} green`);
  }
  if (counts.yellow > 0) {
    statusParts.push(format === 'short' ? `${counts.yellow} y` : `${counts.yellow} yellow`);
  }
  if (counts.red > 0) {
    statusParts.push(format === 'short' ? `${counts.red} r` : `${counts.red} red`);
  }

  // Planning statuses (shown when no progress statuses exist, or alongside them)
  if (counts.planned > 0) {
    statusParts.push(format === 'short' ? `${counts.planned} p` : `${counts.planned} planned`);
  }
  if (counts.tbd > 0) {
    statusParts.push(format === 'short' ? `${counts.tbd} tbd` : `${counts.tbd} to be determined`);
  }

  return statusParts.join(', ');
}
